import type { ComponentDocs } from '../component-docs.js';
import { SPACER_DEFAULT_DIRECTION, SPACER_DEFAULT_SIZE, SPACER_DIRECTIONS, SPACER_SIZES, spacerToken } from './spacer.js';

/**
 * What `<caos-spacer>` says about itself.
 *
 * ── THE SIZES ARE THE SCALE'S, READ FROM THE PIECE ───────────────────────────
 * The `size` input offers exactly `SPACER_SIZES`, the list the stylesheet is written from, so a step the
 * piece draws is a step the reference offers and nothing else (CAOS-474).
 *
 * ── WHAT AN EXAMPLE CANNOT SHOW ──────────────────────────────────────────────
 * A spacer is invisible, so an example shows the space it leaves between two pieces. What it looks like
 * while authoring is described under `placing`, because `authoring` is not an input and an example can
 * only set inputs.
 */
const docs: ComponentDocs = {
  aliases: ['gap', 'space', 'whitespace', 'margin', 'blank space', 'breathing room'],

  answers: [
    'put space between two pieces',
    'push the next piece further down the page',
    'hold two pieces side by side a set distance apart',
    'leave room above a group of pieces',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'size',
      attribute: 'size',
      type: 'text',
      options: [...SPACER_SIZES],
      description:
        'How much space, as a step of the spacing scale: x-small, small, medium, large, x-large, xx-large or ' +
        'xxx-large, from 4px to 48px in the base theme. There is no pixel size, so a spacer follows the ' +
        'theme and cannot drift off the scale.',
      default: SPACER_DEFAULT_SIZE,
    },
    {
      name: 'direction',
      attribute: 'direction',
      type: 'text',
      options: [...SPACER_DIRECTIONS],
      description:
        'vertical is space between pieces stacked one above the other. horizontal is space between pieces ' +
        'side by side in a layout row, and never shrinks.',
      default: SPACER_DEFAULT_DIRECTION,
    },
  ],

  events: [],

  state: [],

  slots: [],

  tokens: SPACER_SIZES.map((size) => ({ token: spacerToken(size), controls: `The ${size} space.` })),

  placing:
    'Place it between the two pieces it separates, in the order they are read. On a running page it draws ' +
    'nothing and leaves only its size. A surface that is authoring the page sets the authoring attribute ' +
    'on it, and it then draws a dashed outline over a tinted fill, so the person building the page can ' +
    'see and select it. authoring is not an input, so no saved page can make a spacer visible to a reader. ' +
    'A horizontal spacer goes in its own LayoutItem between the two columns it separates; give that item ' +
    'a flexibility of no-shrink so a crowded row cannot squeeze the gap out. The spacer holds its own ' +
    'width either way, and takes a height only while authoring, so the page gains no space from it.',

  whenToUse:
    'When two pieces need a deliberate, declared distance between them and nothing else is arranging ' +
    'them: a gap before a group of related pieces, or room between two things in a row.',

  insteadUse: [
    {
      name: 'Layout',
      when: 'You want pieces aligned, centred or spread across a row. A spacer adds space; it aligns nothing.',
    },
    {
      name: 'LayoutItem',
      when: 'The space belongs inside a column, around what it holds, which is the item’s padding.',
    },
  ],

  accessibility:
    'A spacer is hidden from assistive technology, takes no focus and adds nothing to the reading order. ' +
    'A screen reader hears the pieces on either side of it as neighbours. Do not use one to suggest a ' +
    'grouping that a heading or a section should state.',

  examples: [
    {
      key: 'between-two',
      label: 'Space between two pieces',
      summary: 'Two tags stacked, with a large spacer holding them apart.',
      placement: [
        { component: 'caos-tag', children: ['Above'] },
        { component: 'caos-spacer', inputs: { size: 'large' } },
        { component: 'caos-tag', children: ['Below'] },
      ],
    },
    {
      key: 'the-scale',
      label: 'The spacing scale',
      summary: 'Every step of the scale, each spacer under a tag that names its size.',
      placement: SPACER_SIZES.flatMap((size) => [
        { component: 'caos-tag', children: [size] },
        { component: 'caos-spacer', inputs: { size } },
      ]),
    },
    {
      key: 'side-by-side',
      label: 'Side by side',
      summary: 'A horizontal spacer holding two tags apart in a layout row.',
      placement: {
        component: 'caos-layout',
        inputs: { verticalAlign: 'center' },
        children: [
          { component: 'caos-layout-item', children: [{ component: 'caos-tag', children: ['Left'] }] },
          {
            component: 'caos-layout-item',
            inputs: { flexibility: 'no-shrink' },
            children: [{ component: 'caos-spacer', inputs: { direction: 'horizontal', size: 'xx-large' } }],
          },
          { component: 'caos-layout-item', children: [{ component: 'caos-tag', children: ['Right'] }] },
        ],
      },
    },
  ],
};

export default docs;
